import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './queryPointResult';

export interface DroneTargetInfo {
  name: string;
  macAddress: string;
  altitude: string;
  speed: string;
  heading: string;
  latitude: number;
  longitude: number;
  latitudeDegrees: string;
  longitudeDegrees: string;
  operatorLatitude: string;
  operatorLongitude: string;
  imeiScanner: string;
  operatorIdType: string;
  operatorLocationType: string;
  uasId: string;
  uaType: string;
  classificationType: string;
  classEu: string;
  operationStatus: string;
  operationCategoryEu: string;
  messageType: string;
  lastReportTimestamp: Date | null;
  lastOrigin: string;
  targetType: string;
}

const desiredTargetIds: string[] = [
  'mac_address',
  'altitude',
  'speed',
  'heading',
  'operator_latitude',
  'operator_longitude',
  'imei_scanner',
  'operator_id_type',
  'operator_location_type',
  'uas_id',
  'ua_type',
  'classification_type',
  'class_eu',
  'operation_status',
  'operation_category_eu',
  'type',
  'lastorigin',
  // New IDs
  'datetime_last_report',
  'lat_last_report',
  'lon_last_report',
  'datetime_drone_last_report',
  'lat_drone_last_report',
  'lon_drone_last_report',
  // Old IDs (backward compatibility)
  'last_report_timestamp',
];

const desiredTargetNames: string[] = [
  // New names
  'Last Latitude',
  'Last Longitude',
  'Last Report',
  'Last Drone Latitude',
  'Last Drone Longitude',
  'Last Drone Report',
  // Old names (backward compatibility)
  'Latitude',
  'Longitude',
];

// Helper function to extract drone name from source tag
function extractDroneName(lastOrigin: string): string {
  try {
    // Parse the JSON string
    const parsed = JSON.parse(lastOrigin);
    const sourceTag = parsed['Source tag']?.[0];

    if (sourceTag && typeof sourceTag === 'string') {
      // Extract the first part before the first slash
      // e.g., "airplate/DRONE_SENSOR/..." -> "airplate"
      const parts = sourceTag.split('/');
      if (parts.length > 0 && parts[0]) {
        // Capitalize the first letter
        const prefix =
          parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
        return `${prefix} Drone`;
      }
    }
  } catch (error) {
    console.warn('Failed to parse drone source tag:', error);
  }

  // Default fallback
  return 'Drone';
}

export function extractDroneTargetInfos(
  result: QueryPointResult,
): DroneTargetInfo[] {
  const droneTargetInfos: DroneTargetInfo[] = [];

  for (const target of result.targetInfo) {
    const rawDroneTargetInfo: { [key: string]: string } = {};

    for (const target_value of target.values) {
      // Map values by their ID
      if (desiredTargetIds.includes(target_value.id)) {
        rawDroneTargetInfo[target_value.id] = target_value.value;
      }
      // Some values have the same ID so here we map those by the NAME instead
      if (desiredTargetNames.includes(target_value.name)) {
        rawDroneTargetInfo[target_value.name] = target_value.value;
      }
    }

    const lastOrigin = rawDroneTargetInfo['lastorigin'] || '';
    const name = extractDroneName(lastOrigin);

    const droneInfo: DroneTargetInfo = {
      name: name,
      macAddress: rawDroneTargetInfo['mac_address'] || '',
      altitude: rawDroneTargetInfo['altitude'] || '',
      speed: rawDroneTargetInfo['speed'] || '',
      heading: rawDroneTargetInfo['heading'] || '',
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      latitudeDegrees:
        rawDroneTargetInfo['Last Latitude'] ??
        rawDroneTargetInfo['lat_last_report'] ??
        rawDroneTargetInfo['Last Drone Latitude'] ??
        rawDroneTargetInfo['lat_drone_last_report'] ??
        rawDroneTargetInfo['Latitude'] ??
        '',
      longitudeDegrees:
        rawDroneTargetInfo['Last Longitude'] ??
        rawDroneTargetInfo['lon_last_report'] ??
        rawDroneTargetInfo['Last Drone Longitude'] ??
        rawDroneTargetInfo['lon_drone_last_report'] ??
        rawDroneTargetInfo['Longitude'] ??
        '',
      operatorLatitude: rawDroneTargetInfo['operator_latitude'] || '',
      operatorLongitude: rawDroneTargetInfo['operator_longitude'] || '',
      imeiScanner: rawDroneTargetInfo['imei_scanner'] || '',
      operatorIdType: rawDroneTargetInfo['operator_id_type'] || '',
      operatorLocationType: rawDroneTargetInfo['operator_location_type'] || '',
      uasId: rawDroneTargetInfo['uas_id'] || '',
      uaType: rawDroneTargetInfo['ua_type'] || '',
      classificationType: rawDroneTargetInfo['classification_type'] || '',
      classEu: rawDroneTargetInfo['class_eu'] || '',
      operationStatus: rawDroneTargetInfo['operation_status'] || '',
      operationCategoryEu: rawDroneTargetInfo['operation_category_eu'] || '',
      messageType: rawDroneTargetInfo['type'] || '',
      lastReportTimestamp: parseDate(
        rawDroneTargetInfo['datetime_last_report'] ??
          rawDroneTargetInfo['datetime_drone_last_report'] ??
          rawDroneTargetInfo['last_report_timestamp'],
      ),
      lastOrigin: lastOrigin,
      targetType: target['type'],
    };
    droneTargetInfos.push(droneInfo);
  }

  return droneTargetInfos;
}
